import { useState, useEffect, useMemo, useRef } from 'react'
import { coreUtils, ListingSearchField } from '@adc/ui-morpheus'
import type { Maybe, Listing } from '@adc/sdk/types'
import logger from '@adc/logger'
import type { ContextOutput } from '../contextSelector'
import { getListings } from '../../providers/user'
import { extractListingIdFromUrn } from './utils'

// Extract fragment info at module level (computed once)
const LISTINGS_SEARCH_FIELD_FRAGMENT_NAME =
  ListingSearchField[coreUtils.symbols.GRAPH_API_FRAGMENT_REFERENCE].slice(3)
const LISTINGS_SEARCH_FIELD_FRAGMENT_DEFINITION =
  ListingSearchField[coreUtils.symbols.GRAPH_API_FRAGMENT_DEFINITION]

interface UseListingDataResult {
  listing: Maybe<Partial<Listing>>
  isLoading: boolean
  error: Error | null
}

/**
 * Fetches full listing data from a ContextOutput containing listing sources
 */
export const useListingData = (
  initialContext: ContextOutput | null
): UseListingDataResult => {
  const [listing, setListing] = useState<Maybe<Partial<Listing>>>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const previousListingIdRef = useRef<string | null>(null)

  // Extract listing URN and ID, memoized to prevent effect re-runs when context object changes
  const listingUrn = initialContext?.sources?.[0] || null
  const listingId = useMemo(() => {
    return listingUrn ? extractListingIdFromUrn(listingUrn) : null
  }, [listingUrn])

  useEffect(() => {
    // Only clear when switching from one non-null listing to another
    // Don't clear when listingId becomes null (context cleared by parent)
    if (
      listingId !== null &&
      previousListingIdRef.current !== null &&
      listingId !== previousListingIdRef.current
    ) {
      setListing(null)
    }

    // Update ref to track current listingId
    previousListingIdRef.current = listingId

    // Don't fetch if no listingId
    if (!listingId) {
      return
    }

    const fetchListing = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // eslint-disable-next-line
        const result = await getListings({
          // @ts-ignore next
          listingIds: [listingId],
          // @ts-ignore next
          fields: [`fragment:${LISTINGS_SEARCH_FIELD_FRAGMENT_NAME}`],
          // @ts-ignore next
          fieldsConfig: {
            fragmentDefinitions: {
              [LISTINGS_SEARCH_FIELD_FRAGMENT_NAME]: {
                fragment: LISTINGS_SEARCH_FIELD_FRAGMENT_DEFINITION,
              },
            },
          },
          sortBy: {
            field: 'created_date',
            order: 'desc',
          },
        })

        // eslint-disable-next-line
        const fetchedListing = result?.data?.seek?.content?.[0] as Maybe<
          Partial<Listing>
        >

        if (fetchedListing) {
          setListing(fetchedListing)
        } else {
          logger.warn(`Listing ${listingId} not found, using minimal object`)
          setListing({ listing_id: listingId })
        }
      } catch (err) {
        logger.error('Failed to fetch listing data:', err)
        setError(err as Error)
        setListing({ listing_id: listingId }) // Fallback on error
      } finally {
        setIsLoading(false)
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchListing()
  }, [listingId])

  return { listing, isLoading, error }
}
